import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Modal, Button, Input, Typography, Space, Form, Select, Flex, Card } from "antd"
import { EditOutlined, DeleteOutlined, CloseOutlined } from "@ant-design/icons"
import { Text } from "ui/Typography"
import IconWithBg from "ui/IconWithBg"
import styles from "./index.module.css"
import { getGuideCategoryToBoats, batchManageCategories, checkDuplicateCategoryNameAdd, checkDuplicateCategoryNameEdit } from "store/boats"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import { useTranslation } from "react-i18next"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"

export default function ManageCategoryModal({ open, onCancel, onUpdate }) {
  ManageCategoryModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired
  }
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const posting = useSelector((state) => state.boats?.category_to_boats?.loading)
  const categoryListing = useSelector((state) => state.boats.category_to_boats.items)
  const checkingDuplicateAdd = useSelector((state) => state.boats?.check_duplicate_category_add?.loading)
  const checkingDuplicateEdit = useSelector((state) => state.boats?.check_duplicate_category_edit?.loading)

  // Store original categories for cancel/revert functionality
  const [originalCategories, setOriginalCategories] = useState([])
  const [categories, setCategories] = useState([])
  const [pendingOperations, setPendingOperations] = useState([])

  const [addForm] = Form.useForm()
  const [editForm] = Form.useForm()
  const [editingIndex, setEditingIndex] = useState(null)
  const [originalEditName, setOriginalEditName] = useState("")
  const [isEditDirty, setIsEditDirty] = useState(false)
  const [isAddVisible, setIsAddVisible] = useState(false)
  const [deleteIndex, setDeleteIndex] = useState(null)
  const [moveTargetId, setMoveTargetId] = useState(null)

  // Fetch categories on mount
  useEffect(() => {
    dispatch(getGuideCategoryToBoats())
  }, [dispatch])

  // Sync local categories when store updates and reset pending operations
  useEffect(() => {
    if (Array.isArray(categoryListing)) {
      const mappedCategories = categoryListing.map((c) => ({ id: c.id, name: c.name, items: c.boats_count ?? 0 }))
      setOriginalCategories(mappedCategories)
      setCategories(mappedCategories)
      setPendingOperations([])
    }
  }, [categoryListing])
  // Add category locally (check duplicate before saving)
  const handleAdd = async (values) => {
    const trimmedName = values.name.trim()

    if (categories.some((cat) => cat.name.toLowerCase() === trimmedName.toLowerCase())) {
      const errorMessage = t("equipment.modal_errors.category_name_exists")
      addForm.setFields([
        {
          name: "name",
          errors: [errorMessage]
        }
      ])
      return
    }

    // Check for duplicate before saving
    dispatch(checkDuplicateCategoryNameAdd(trimmedName))
      .then((response) => {
        // Check if the name is unique
        if (!response.is_unique) {
          // Name is duplicate
          const errorMessage = t("equipment.modal_errors.category_name_exists")
          addForm.setFields([
            {
              name: "name",
              errors: [errorMessage]
            }
          ])
          return
        }

        // Name is unique, proceed with adding
        const newCategory = {
          id: `temp_${Date.now()}`, // Temporary ID for local tracking
          name: values.name,
          items: 0,
          isNew: true // Mark as new for later processing
        }

        setCategories((prev) => [...prev, newCategory])
        setPendingOperations((prev) => [
          ...prev,
          {
            type: "add",
            category: { name: values.name }
          }
        ])

        setIsAddVisible(false)
        addForm.resetFields()
      })
      .catch((error) => {
        // API error
        const errorMessage = error?.message || t("equipment.modal_errors.failed_check_category")
        addForm.setFields([
          {
            name: "name",
            errors: [errorMessage]
          }
        ])
      })
  }

  // Edit category locally (check duplicate before saving)
  const handleEdit = async (index, values) => {
    const trimmedName = values.name.trim()

    if (categories.some((cat, i) => i !== index && cat.name.toLowerCase() === trimmedName.toLowerCase())) {
      const errorMessage = t("equipment.modal_errors.category_name_exists")
      editForm.setFields([
        {
          name: "name",
          errors: [errorMessage]
        }
      ])
      return
    }

    // Check for duplicate before saving
    dispatch(checkDuplicateCategoryNameEdit(trimmedName))
      .then((response) => {
        // Check if the name is unique
        if (!response.is_unique) {
          // Name is duplicate
          const errorMessage = t("equipment.modal_errors.category_name_exists")
          editForm.setFields([
            {
              name: "name",
              errors: [errorMessage]
            }
          ])
          return
        }

        // Name is unique, proceed with editing
        const updated = [...categories]
        const target = updated[index]

        updated[index] = { ...target, name: values.name }
        setCategories(updated)

        // Only add to pending operations if it's not a new category
        if (target.isNew) {
          // Update the pending add operation
          setPendingOperations((prev) =>
            prev.map((op) => (op.type === "add" && op.category.name === target.name ? { ...op, category: { name: values.name } } : op))
          )
        } else {
          setPendingOperations((prev) => [
            ...prev,
            {
              type: "edit",
              category: {
                id: target.id,
                name: values.name
              }
            }
          ])
        }

        setEditingIndex(null)
        editForm.resetFields()
      })
      .catch((error) => {
        // API error
        const errorMessage = error?.message || t("equipment.modal_errors.failed_check_category")
        editForm.setFields([
          {
            name: "name",
            errors: [errorMessage]
          }
        ])
      })
  }

  const handleDelete = (index) => {
    setDeleteIndex(index)
  }

  // Delete category locally (no API call)
  const confirmDelete = (index) => {
    const target = categories[index]

    // Only require moveTargetId if category has items
    if (target.items > 0 && !moveTargetId) {
      toaster.error(t("equipment.modal_errors.select_category_to_move"))
      return
    }

    // Remove from local state
    setCategories((prev) => prev.filter((_, i) => i !== index))

    // Only add to pending operations if it's not a new category
    if (target.isNew) {
      // Remove the pending add operation for new categories
      setPendingOperations((prev) => prev.filter((op) => !(op.type === "add" && op.category.name === target.name)))
    } else {
      const deleteOperation = {
        type: "delete",
        category: {
          id: target.id,
          _destroy: true
        }
      }

      // Only include move_to_category_id if category has items
      if (target.items > 0 && moveTargetId) {
        deleteOperation.move_to_category_id = moveTargetId
      }

      setPendingOperations((prev) => [...prev, deleteOperation])
    }

    setDeleteIndex(null)
    setMoveTargetId(null)
  }

  // Handle cancel - revert to original state
  const handleCancel = () => {
    setCategories([...originalCategories])
    setPendingOperations([])
    setEditingIndex(null)
    setIsAddVisible(false)
    setDeleteIndex(null)
    setMoveTargetId(null)
    addForm.resetFields()
    editForm.resetFields()
    onCancel()
  }

  // Handle update - batch all operations and call API
  const handleUpdate = () => {
    if (pendingOperations.length === 0) {
      toaster.info("No changes to save")
      onUpdate()
      return
    }

    // Format operations into categories array
    const categories = pendingOperations
      .map((op) => {
        if (op.type === "add") {
          // Add operation: only name
          return { name: op.category.name }
        } else if (op.type === "edit") {
          // Edit operation: id and name
          return { id: op.category.id, name: op.category.name }
        } else if (op.type === "delete") {
          // Delete operation: id, _destroy, and optionally move_to_category_id
          const deleteData = { id: op.category.id, _destroy: true }
          if (op.move_to_category_id) {
            deleteData.move_to_category_id = op.move_to_category_id
          }
          return deleteData
        }
        return null
      })
      .filter(Boolean)

    const payload = { categories }

    // Call the batch API using Redux action
    dispatch(batchManageCategories(payload))
      .then((response) => {
        toaster.success(response?.message || "Categories updated successfully")
        dispatch(getGuideCategoryToBoats())
        onUpdate()
      })
      .catch((error) => {
        toaster.error(error?.message || "Failed to update categories")
      })
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title={<Typography.Title level={4}>{t("equipment.manage_categories")}</Typography.Title>}
      footer={null}
      width={800}
      centered
      className={styles.manageCategoriesModal}
      maskClosable={false}
    >
      <FirstLoading loading={posting}>
        {!isAddVisible && (
          <Flex className={styles.addCategoryWrapper}>
            <Button type="primary" onClick={() => setIsAddVisible(true)} className={styles.addCategoryBtn}>
              {t("equipment.add_category")}
            </Button>
          </Flex>
        )}

        <Space direction="vertical" className={styles.verticalSpace}>
          {/* Add new category */}

          {isAddVisible && (
            <div className={styles.addCategoryModal}>
              <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                <Flex justify="space-between" align="center" className={styles.addCategoryTitle}>
                  <Text size="lg" fw={500}>
                    {t("equipment.add_new_category")}
                  </Text>
                  <IconWithBg
                    icon={<CloseOutlined />}
                    className={styles.cancelBtn}
                    onClick={() => {
                      setIsAddVisible(false)
                      addForm.resetFields()
                    }}
                    aria-label={t("equipment.cancel")}
                  />
                </Flex>
                <div className={styles.addCategoryForm}>
                  <LabeledField
                    name="name"
                    labelKey={t("equipment.category_name")}
                    rules={[{ required: true, message: t("equipment.please_enter_category_name") }]}
                  >
                    <Input
                      autoFocus
                      placeholder="Category name"
                      className={styles.categoryInput}
                      onChange={() => addForm.setFields([{ name: "name", errors: [] }])}
                    />
                  </LabeledField>
                  <Space className="mt-xs">
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setIsAddVisible(false)
                        addForm.resetFields()
                      }}
                    >
                      {t("equipment.cancel")}
                    </Button>
                    <Button type="primary" htmlType="submit" loading={checkingDuplicateAdd} disabled={checkingDuplicateAdd}>
                      {checkingDuplicateAdd ? "Saving..." : "Save"}
                    </Button>
                  </Space>
                </div>
              </Form>
            </div>
          )}
          {/* Category list */}
          {categories.map((cat, idx) => (
            <Card key={cat.id || idx} className={styles.categoryCard} bodyStyle={{ padding: 0 }}>
              <Flex align="center" justify="space-between" className={styles.cardFlex}>
                <div className={styles.categoryInfo}>
                  <span className={styles.categoryName}>{cat.name}</span>
                  <span className={styles.categoryItems}>({cat.items} items)</span>
                </div>
                {editingIndex === idx || deleteIndex === idx ? (
                  <IconWithBg
                    icon={<CloseOutlined />}
                    className={styles.editBtn}
                    onClick={() => {
                      if (editingIndex === idx) {
                        setEditingIndex(null)
                        editForm.resetFields()
                      } else if (deleteIndex === idx) {
                        setDeleteIndex(null)
                        setMoveTargetId(null)
                      }
                    }}
                    aria-label={t("equipment.cancel")}
                  />
                ) : (
                  <Space size={16}>
                    <IconWithBg
                      icon={<EditOutlined />}
                      className={styles.editBtn}
                      onClick={() => {
                        setDeleteIndex(null)
                        setEditingIndex(idx)
                        setOriginalEditName(cat.name || "")
                        setIsEditDirty(false)
                        editForm.setFieldsValue({ name: cat.name })
                      }}
                      aria-label={t("equipment.edit")}
                    />
                    <IconWithBg
                      icon={<DeleteOutlined />}
                      className={styles.deleteBtn}
                      onClick={() => {
                        setEditingIndex(null)
                        handleDelete(idx)
                      }}
                      aria-label={t("equipment.delete")}
                    />
                  </Space>
                )}
              </Flex>
              {editingIndex === idx && (
                <div className={styles.editCategoryModal}>
                  <div className={styles.editCategoryTitle}>{t("equipment.edit_category")}</div>
                  <Form
                    form={editForm}
                    layout="vertical"
                    onFinish={(values) => handleEdit(idx, values)}
                    initialValues={{ name: cat.name }}
                    onValuesChange={(changed, all) => {
                      const current = all.name || ""
                      setIsEditDirty(current.trim() !== (originalEditName || "").trim())
                    }}
                  >
                    <LabeledField
                      name="name"
                      labelKey={t("equipment.category_name")}
                      rules={[{ required: true, message: t("equipment.please_enter_category_name") }]}
                    >
                      <Input
                        autoFocus
                        className={styles.categoryInput}
                        onChange={() => editForm.setFields([{ name: "name", errors: [] }])}
                      />
                    </LabeledField>
                    <Space>
                      <Button variant="outlined" color="primary" onClick={() => setEditingIndex(null)}>{t("equipment.cancel")}</Button>
                      <Button type="primary" htmlType="submit" loading={checkingDuplicateEdit} disabled={!isEditDirty || checkingDuplicateEdit}>
                        {checkingDuplicateEdit ? "Saving..." : t("equipment.save")}
                      </Button>
                    </Space>
                  </Form>
                </div>
              )}
              {deleteIndex === idx && (
                <div className={styles.editCategoryModal}>
                  <div className={styles.editCategoryTitle}>{t("equipment.delete_category")}</div>
                  {cat.items > 0 ? (
                    <>
                      <Text>All {cat.items} items inside category will be moved to:</Text>
                      <Form layout="vertical" className="mt-m">
                        <Form.Item>
                          <Text fw={500}>Category</Text>
                          <Select
                            style={{ width: "100%" }}
                            placeholder="Select category"
                            value={moveTargetId || undefined}
                            onChange={(val) => setMoveTargetId(val)}
                          >
                            {categories
                              .filter((_, i) => i !== idx)
                              .map((c) => (
                                <Select.Option key={c.id || c.name} value={c.id}>
                                  {c.name}
                                </Select.Option>
                              ))}
                          </Select>
                        </Form.Item>
                      </Form>
                    </>
                  ) : (
                    <Text>This category has no items and can be safely deleted.</Text>
                  )}
                  <Flex justify="start" gap={5} className={styles.footerFlex}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setDeleteIndex(null)
                        setMoveTargetId(null)
                      }}
                    >
                      {t("equipment.cancel")}
                    </Button>
                    <Button
                      type="primary"
                      danger
                      onClick={() => confirmDelete(idx)}
                      loading={posting}
                      disabled={posting || (cat.items > 0 && !moveTargetId)}
                    >
                      {cat.items > 0 && moveTargetId ? t("equipment.move_items_and_delete_category") : t("equipment.delete")}
                    </Button>
                  </Flex>
                </div>
              )}
            </Card>
          ))}

          <Flex justify="start" gap={12} className={styles.footerFlex}>
            <Button variant="outlined" color="primary" onClick={handleCancel} className={styles.footerCancelBtn}>
              {t("equipment.cancel")}
            </Button>
            <Button
              type="primary"
              onClick={handleUpdate}
              className={styles.footerUpdateBtn}
              loading={posting}
              disabled={pendingOperations.length === 0 || posting}
            >
              {t("equipment.update")}
            </Button>
          </Flex>
        </Space>
      </FirstLoading>
    </Modal>
  )
}
